/* eslint-disable @typescript-eslint/no-explicit-any */
import type { IDV, VendorEventPayload } from '@/integrations/FrankieOne/types';
import * as Sentry from '@sentry/vue';
import { config } from '@/config';
import Mixpanel from '@/integrations/Mixpanel';
import type { OneSdkErrorEvent } from '@/components/session/kyc/idv/OneSdkErrorEvent';

type OneSdkCallback = (message: any) => void | Promise<void>;
type OneSdkErrorCallback = (error: OneSdkErrorEvent) => void | Promise<void>;
type OneSdkEventCallback = (callback: OneSdkCallback) => void;
interface OneSdkEvents {
  onReadyCallback: OneSdkEventCallback[];
  onResultsCallback: OneSdkEventCallback[];
  onSessionDataFailedLoadingCallback: OneSdkEventCallback[];
  onErrorCallback: OneSdkErrorCallback[];
  onInputRequiredCallback: OneSdkEventCallback[];
  onUnsupportedDocumentCallback: OneSdkEventCallback[];
  onSessionInterruptedCallback: OneSdkEventCallback[];
  onLoadingCallback: OneSdkEventCallback[];
  onDetectionCompleteCallback: OneSdkEventCallback[];
  onProcessingCallback: OneSdkEventCallback[];
  onWarningCallback: OneSdkEventCallback[];
  onInfoCallback: OneSdkEventCallback[];
  onDetectionFailedCallback: OneSdkEventCallback[];
  onVendorEventCallback: OneSdkEventCallback[];
}

function logIDVEvent(payload: any) {
  if (config.idvVendorDebugMode) {
    // eslint-disable-next-line no-console
    console.debug('OneSdk event: ', payload);
  }
}

function isIdvComplete(payload: any): boolean {
  //https://docs.frankieone.com/v1.14.1/docs/implementation-guide/module/idv#result-events
  return (
    ['COMPLETE', 'FAILED'].includes(payload.checkStatus?.toUpperCase()) || // Incode
    payload.checkStatus?.toUpperCase() === 'CHECK_PROCESSING' // Onfido
  );
}

function isDocumentUnsupported(payload: any): boolean {
  return payload.checkStatus === 'AWAITING_DOCUMENT_UPLOAD_INVALID_TYPE';
}

function getCompletedIdvStep(payload: VendorEventPayload) {
  switch (payload.eventName) {
    case 'DOCUMENT_TYPE_SELECT':
      return 'DocumentUploadStarted';
    case 'UPLOAD':
      if (payload.properties?.document_side === 'front') {
        return 'DocumentFrontSideUploaded';
      }
      return undefined;
    case 'DOCUMENT_CAPTURE_CONFIRMATION_BACK':
      return 'DocumentBackSideUploaded';
    case 'MOTION_FACIAL_INTRO':
      return 'BiometricsStarted';
    case 'MOTION_FACIAL_UPLOAD_COMPLETED':
      return 'BiometricsCompleted';
    default:
      return undefined;
  }
}

export function useFrankieOneIDVEvent(idv: IDV) {
  const callbacks: OneSdkEvents = {
    onResultsCallback: [],
    onReadyCallback: [],
    onSessionDataFailedLoadingCallback: [],
    onDetectionCompleteCallback: [],
    onErrorCallback: [],
    onDetectionFailedCallback: [],
    onInputRequiredCallback: [],
    onUnsupportedDocumentCallback: [],
    onSessionInterruptedCallback: [],
    onLoadingCallback: [],
    onProcessingCallback: [],
    onWarningCallback: [],
    onInfoCallback: [],
    onVendorEventCallback: [],
  };

  idv.on('*', (payload: any) => {
    logIDVEvent(payload);
  });

  idv.on('ready', (payload: any) => {
    if (callbacks.onReadyCallback.length > 0) {
      callbacks.onReadyCallback.forEach((callback) => callback(payload));
    }
  });

  idv.on('results', (payload: any) => {
    const isComplete = isIdvComplete(payload);

    if (!isComplete) {
      // This should not happen. If it does we need to investigate.
      Sentry.captureException(
        `Received unexpected FrankieOne IDV check results status ${payload?.checkStatus}"`
      );
    }
    if (callbacks.onResultsCallback.length > 0) {
      callbacks.onResultsCallback.forEach((callback) => callback({ isComplete, ...payload }));
    }
  });

  idv.on('session_data_failed_loading', (payload: any) => {
    if (callbacks.onSessionDataFailedLoadingCallback.length > 0) {
      callbacks.onSessionDataFailedLoadingCallback.forEach((callback) => callback(payload));
    }
  });

  idv.on('error', (payload: OneSdkErrorEvent) => {
    if (callbacks.onErrorCallback.length > 0) {
      callbacks.onErrorCallback.forEach((callback) => callback(payload));
    }
  });

  idv.on('input_required', (payload: any) => {
    const isUnsupportedDocument = isDocumentUnsupported(payload);
    if (isUnsupportedDocument) {
      Sentry.captureMessage('Unsupported document provided', {
        extra: {
          payload,
        },
      });
      if (callbacks.onUnsupportedDocumentCallback.length > 0) {
        callbacks.onUnsupportedDocumentCallback.forEach((callback) =>
          callback({ isUnsupportedDocument, ...payload })
        );
      }
    } else {
      Sentry.captureMessage("Received 'input_required' event from FrankieOne OneSdk", {
        level: 'error',
        extra: {
          payload,
        },
      });
      if (callbacks.onInputRequiredCallback.length > 0) {
        callbacks.onInputRequiredCallback.forEach((callback) => callback(payload));
      }
    }
  });

  idv.on('session_interrupted', (payload: any) => {
    if (callbacks.onSessionInterruptedCallback.length > 0) {
      callbacks.onSessionInterruptedCallback.forEach((callback) => callback(payload));
    }
  });

  idv.on('loading', (payload: any) => {
    if (callbacks.onLoadingCallback.length > 0) {
      callbacks.onLoadingCallback.forEach((callback) => callback(payload));
    }
  });

  idv.on('detection_complete', (payload: any) => {
    if (callbacks.onDetectionCompleteCallback.length > 0) {
      callbacks.onDetectionCompleteCallback.forEach((callback) => callback(payload));
    }
  });

  idv.on('processing', (payload: any) => {
    Sentry.captureMessage("Received 'processing' event from FrankieOne OneSdk", {
      extra: {
        payload,
      },
    });
    if (callbacks.onProcessingCallback.length > 0) {
      callbacks.onProcessingCallback.forEach((callback) => callback(payload));
    }
  });

  idv.on('warning', (payload: any) => {
    Sentry.captureMessage("Received 'warning' event from FrankieOne OneSdk", {
      extra: {
        payload,
      },
    });

    if (callbacks.onWarningCallback.length > 0) {
      callbacks.onWarningCallback.forEach((callback) => callback(payload));
    }
  });

  idv.on('info', (payload: any) => {
    Sentry.captureMessage("Received 'info' event from FrankieOne OneSdk", {
      extra: {
        payload,
      },
    });
    if (callbacks.onInfoCallback.length > 0) {
      callbacks.onInfoCallback.forEach((callback) => callback(payload));
    }
  });

  idv.on('detection_failed', (payload: any) => {
    Sentry.captureMessage("Received 'detection_failed' event from FrankieOne OneSdk", {
      level: 'error',
      extra: {
        payload,
      },
    });
    if (callbacks.onDetectionFailedCallback.length > 0) {
      callbacks.onDetectionFailedCallback.forEach((callback) => callback(payload));
    }
  });

  idv.on('vendor_event', (payload: any) => {
    const completedIdvStep = getCompletedIdvStep(payload);
    if (completedIdvStep) {
      Mixpanel.instance.trackIdvStepCompleted(completedIdvStep);
    }
    if (callbacks.onVendorEventCallback.length > 0) {
      callbacks.onVendorEventCallback.forEach((callback) => callback(payload));
    }
  });

  return {
    onReady: (cb: OneSdkCallback) => {
      callbacks.onReadyCallback.push(cb);
    },
    onResults: (cb: OneSdkCallback) => {
      callbacks.onResultsCallback.push(cb);
    },
    onSessionDataFailedLoading: (cb: OneSdkCallback) => {
      callbacks.onSessionDataFailedLoadingCallback.push(cb);
    },
    onDetectionComplete: (cb: OneSdkCallback) => {
      callbacks.onDetectionCompleteCallback.push(cb);
    },
    onError: (cb: OneSdkErrorCallback) => {
      callbacks.onErrorCallback.push(cb);
    },
    onDetectionFailed: (cb: OneSdkCallback) => {
      callbacks.onDetectionFailedCallback.push(cb);
    },
    onInputRequired: (cb: OneSdkCallback) => {
      callbacks.onInputRequiredCallback.push(cb);
    },
    onUnsupportedDocument: (cb: OneSdkCallback) => {
      callbacks.onUnsupportedDocumentCallback.push(cb);
    },
    onSessionInterrupted: (cb: OneSdkCallback) => {
      callbacks.onSessionInterruptedCallback.push(cb);
    },
    onLoading: (cb: OneSdkCallback) => {
      callbacks.onLoadingCallback.push(cb);
    },
    onProcessing: (cb: OneSdkCallback) => {
      callbacks.onProcessingCallback.push(cb);
    },
    onWarning: (cb: OneSdkCallback) => {
      callbacks.onWarningCallback.push(cb);
    },
    onInfo: (cb: OneSdkCallback) => {
      callbacks.onInfoCallback.push(cb);
    },
    onVendorEvent: (cb: OneSdkCallback) => {
      callbacks.onVendorEventCallback.push(cb);
    },
    triggerEvent: (eventName: keyof OneSdkEvents, message: any) => {
      callbacks[eventName].forEach((callback) => callback(message));
    },
  };
}
